import React, { useState, useEffect, useCallback } from 'react';
import { clientProjects, ownProjects, projectFilters } from '../data/projects';
import '../styles/projects.css';

const ProjectIcon = ({ type }) => {
  const icons = {
    server: (
      <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="1.2">
        <rect x="2" y="2" width="20" height="8" rx="2" /><rect x="2" y="14" width="20" height="8" rx="2" />
        <line x1="6" y1="6" x2="6.01" y2="6" /><line x1="6" y1="18" x2="6.01" y2="18" />
      </svg>
    ),
    code: (
      <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="1.2">
        <polyline points="16 18 22 12 16 6" /><polyline points="8 6 2 12 8 18" />
      </svg>
    ),
    wifi: (
      <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="1.2">
        <path d="M5 12.55a11 11 0 0 1 14 0" /><path d="M1.42 9a16 16 0 0 1 21.16 0" />
        <path d="M8.53 16.11a6 6 0 0 1 6.95 0" /><line x1="12" y1="20" x2="12.01" y2="20" />
      </svg>
    ),
    shield: (
      <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="1.2">
        <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
      </svg>
    ),
    leaf: (
      <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="1.2">
        <path d="M11 20A7 7 0 0 1 9.8 6.1C15.5 5 17 4.48 19 2c1 2 2 4.18 2 8 0 5.5-4.78 10-10 10z" />
        <path d="M2 21c0-3 1.85-5.36 5.08-6C9.5 14.52 12 13 13 12" />
      </svg>
    ),
    legal: (
      <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="1.2">
        <path d="M12 3v18" /><path d="M5 7h14" /><path d="M7 7l-2 14h14l-2-14" />
      </svg>
    ),
    chart: (
      <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="1.2">
        <path d="M3 3v18h18" /><path d="M7 16l4-4 4 4 5-8" />
      </svg>
    ),
    education: (
      <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="1.2">
        <path d="M22 10v6M2 10l10-5 10 5-10 5z" /><path d="M6 12v5c0 2 3 3 6 3s6-1 6-3v-5" />
      </svg>
    )
  };
  return icons[type] || icons.code;
};

const ProjectModal = ({ project, onClose }) => {
  useEffect(() => {
    const handleKey = (e) => {
      if (e.key === 'Escape') onClose();
    };
    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', handleKey);
    return () => {
      document.body.style.overflow = '';
      window.removeEventListener('keydown', handleKey);
    };
  }, [onClose]);

  if (!project) return null;

  return (
    <div className="project-modal-overlay" onClick={onClose} role="presentation">
      <div
        className="project-modal"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="project-modal-title"
      >
        <div className={`project-modal-hero ${project.imageClass}`}>
          <div className="project-modal-hero-grid" />
          <ProjectIcon type={project.icon} />
          <button type="button" className="project-modal-close" onClick={onClose} aria-label="Fermer">
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>

        <div className="project-modal-body">
          <div className="project-modal-tags">
            <span className={`project-category ${project.badgeClass}`}>{project.category}</span>
            {project.type === 'own' && <span className="project-own-badge">Solution Nexus</span>}
          </div>

          <h2 id="project-modal-title" className="project-modal-title">
            {project.title}
            {project.subtitle && <span className="project-modal-subtitle">{project.subtitle}</span>}
          </h2>

          <div className="project-modal-client">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" /><circle cx="12" cy="7" r="4" />
            </svg>
            {project.client}
          </div>

          <div className="project-modal-section">
            <h3>Contexte</h3>
            <p>{project.context}</p>
          </div>

          <div className="project-modal-section">
            <h3>Prestations réalisées</h3>
            <ul className="project-modal-list">
              {project.deliverables.map((item, i) => (
                <li key={i}>{item}</li>
              ))}
            </ul>
          </div>

          <div className="project-modal-section">
            <h3>Technologies &amp; compétences</h3>
            <div className="project-modal-tech">
              {project.technologies.map((tech, i) => (
                <span key={i} className="tech-tag">{tech}</span>
              ))}
            </div>
          </div>

          <div className="project-modal-actions">
            {project.link && (
              <a href={project.link} target="_blank" rel="noopener noreferrer" className="btn btn-primary">
                Visiter le site
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
                  <polyline points="15 3 21 3 21 9" /><line x1="10" y1="14" x2="21" y2="3" />
                </svg>
              </a>
            )}
            <a href="#contact" className="btn btn-secondary" onClick={onClose}>
              Discuter d'un projet similaire
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

const ProjectCard = ({ project, onOpen, index }) => (
  <article
    className="project-card-v2"
    style={{ animationDelay: `${index * 60}ms` }}
  >
    <button type="button" className="project-card-v2-inner" onClick={() => onOpen(project)}>
      <div className={`project-image ${project.imageClass}`}>
        <ProjectIcon type={project.icon} />
        {project.type === 'own' && <span className="project-card-own-label">Nexus</span>}
      </div>
      <div className="project-body">
        <div className="project-card-meta">
          <span className={`project-category ${project.badgeClass}`}>{project.category}</span>
        </div>
        <h3>
          {project.title}
          {project.subtitle && <span className="project-card-subtitle">{project.subtitle}</span>}
        </h3>
        <p className="project-card-client">{project.client}</p>
        <p className="project-card-excerpt">{project.context}</p>
        <div className="project-card-footer">
          <span className="project-result">{project.highlight}</span>
          <span className="project-card-cta">
            Voir le détail
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4">
              <line x1="5" y1="12" x2="19" y2="12" /><polyline points="12 5 19 12 12 19" />
            </svg>
          </span>
        </div>
      </div>
    </button>
  </article>
);

const ProjectsSection = () => {
  const [activeTab, setActiveTab] = useState('client');
  const [activeFilter, setActiveFilter] = useState('all');
  const [selectedProject, setSelectedProject] = useState(null);

  const projects = activeTab === 'client' ? clientProjects : ownProjects;
  const filters = projectFilters[activeTab];

  const filtered = activeFilter === 'all'
    ? projects
    : projects.filter((p) => p.categoryKey === activeFilter);

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setActiveFilter('all');
  };

  const closeModal = useCallback(() => setSelectedProject(null), []);

  return (
    <section id="work" className="projects-section">
      <div className="projects-section-inner">
        <div className="section-header">
          <span className="section-label">Réalisations</span>
          <h2>Projets réalisés pour des clients &amp; solutions propres</h2>
          <p>
            De la microfinance au télécom, en passant par le waste management et nos propres plateformes numériques — une expertise ancrée sur le terrain camerounais.
          </p>
        </div>

        <div className="projects-tabs" role="tablist">
          <button
            type="button"
            role="tab"
            aria-selected={activeTab === 'client'}
            className={`projects-tab ${activeTab === 'client' ? 'active' : ''}`}
            onClick={() => handleTabChange('client')}
          >
            <span className="projects-tab-icon">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" />
                <path d="M23 21v-2a4 4 0 0 0-3-3.87" /><path d="M16 3.13a4 4 0 0 1 0 7.75" />
              </svg>
            </span>
            Projets clients
            <span className="projects-tab-count">{clientProjects.length}</span>
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={activeTab === 'own'}
            className={`projects-tab ${activeTab === 'own' ? 'active' : ''}`}
            onClick={() => handleTabChange('own')}
          >
            <span className="projects-tab-icon">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
              </svg>
            </span>
            Solutions propres
            <span className="projects-tab-count">{ownProjects.length}</span>
          </button>
        </div>

        <div className="projects-filters">
          {filters.map((filter) => (
            <button
              key={filter.id}
              type="button"
              className={`projects-filter ${activeFilter === filter.id ? 'active' : ''}`}
              onClick={() => setActiveFilter(filter.id)}
            >
              {filter.label}
            </button>
          ))}
        </div>

        <div className="projects-grid-v2" key={`${activeTab}-${activeFilter}`}>
          {filtered.map((project, index) => (
            <ProjectCard
              key={project.id}
              project={project}
              index={index}
              onOpen={setSelectedProject}
            />
          ))}
        </div>

        {filtered.length === 0 && (
          <p className="projects-empty">Aucun projet dans cette catégorie pour le moment.</p>
        )}

        <div className="projects-cta-banner">
          <div>
            <h3>Un projet ambitieux à mener ?</h3>
            <p>Infrastructure, logiciel, cybersécurité ou plateforme sur-mesure — construisons-le ensemble.</p>
          </div>
          <a href="#contact" className="btn btn-primary">
            Démarrer un projet
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2">
              <line x1="5" y1="12" x2="19" y2="12" /><polyline points="12 5 19 12 12 19" />
            </svg>
          </a>
        </div>
      </div>

      {selectedProject && (
        <ProjectModal project={selectedProject} onClose={closeModal} />
      )}
    </section>
  );
};

export default ProjectsSection;
